import styles from "./StockCounselList.module.scss"

import React, {
  memo,
  MouseEvent,
  useCallback,
  useEffect,
  useState,
} from "react"
import { useLocation, useParams } from "react-router-dom"

import { stockCounselApi } from "@shared/apis"
import { CustomLink } from "@shared/components"
import { GROUP } from "@shared/config"
import { useAccount, useSchedule } from "@shared/contexts"
import { getRef } from "@shared/modules"

import classNames from "classnames"
import { Vod } from "vod/contexts"

const STREAMER_CHATTING = "streamer-chatting"

interface Props {
  hide?: boolean
  forStreamer?: boolean
}

export default memo(function StockCounselsList({ hide, forStreamer }: Props) {
  const { pathname } = useLocation()
  const { schedule } = useSchedule()
  const { account } = useAccount()

  const [stockCounsels, setStockCounsels] = useState<StockCounsel[]>([])
  const [currentStockCounselId, setCurrentStockCounselId] = useState<number>()

  const recording = useCallback(
    (e: MouseEvent, stockCounselStatus: Status, stockCounselId: number) => {
      e.preventDefault()

      if (!GROUP.STREAMER.includes(account.viewer!.role)) return

      if (stockCounselStatus === "WAITING") {
        stockCounselApi.recodingStart(stockCounselId)
        setCurrentStockCounselId(stockCounselId)
        return
      }

      if (stockCounselStatus === "RECORDING") {
        stockCounselApi.recodingEnd(stockCounselId)
        setCurrentStockCounselId(undefined)
        return
      }
    },
    [account],
  )

  useEffect(() => {
    if (!schedule) return

    const viewersRef = getRef("stock_counsels")
      .child(schedule.key)
      .orderByChild("timestamp")

    viewersRef.on("value", (snapshot: firebase.database.DataSnapshot) => {
      const counsels = snapshot.val()

      if (!counsels) return setStockCounsels([])

      setStockCounsels(
        Object.keys(counsels).map(
          (key: string) =>
            ({
              id: counsels[key].id,
              vodId: counsels[key].vod_id,
              stockName: counsels[key].stock_name,
              requestCount: counsels[key].request_count,
              stockCounselStatus: counsels[key].stock_counsel_status,

              createdAt: counsels[key].created_at,
            } as StockCounsel),
        ),
      )
    })

    return () => {
      viewersRef.off("value")
    }
  }, [schedule])

  useEffect(() => {
    const endRecording = (e: Event) => {
      e.preventDefault()

      if (
        !account.viewer ||
        !GROUP.STREAMER.includes(account.viewer.role) ||
        !currentStockCounselId ||
        !pathname.includes(STREAMER_CHATTING)
      ) {
        return
      }

      stockCounselApi.recodingEnd(currentStockCounselId)
    }

    window.addEventListener("beforeunload", endRecording)

    return () => {
      window.removeEventListener("beforeunload", endRecording)
    }
  }, [account.viewer, currentStockCounselId, pathname])

  return (
    <div
      className={classNames("tableB", {
        hidden: hide,
      })}
    >
      <table>
        <colgroup>
          <col width="70" />
          <col width="*" />
          <col width="70" />
        </colgroup>
        <thead>
          <tr>
            <th>보기</th>
            <th>종목명</th>
            <th>신청수</th>
          </tr>
        </thead>
      </table>
      <div
        className={classNames("inner", [
          forStreamer ? styles.streamer : styles.expand,
        ])}
      >
        <table>
          <colgroup>
            <col width="70" />
            <col width="*" />
            <col width="70" />
          </colgroup>
          <tbody>
            {stockCounsels.map((stockCounsel: StockCounsel) => (
              <StockCounselRow
                key={stockCounsel.id}
                stockCounsel={stockCounsel}
                recording={recording}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
})

interface StockCounselProps {
  stockCounsel: StockCounsel
  recording(
    e: MouseEvent,
    stockCounselStatus: Status,
    stockCounselId: number,
  ): void
}

function StockCounselRow({ stockCounsel, recording }: StockCounselProps) {
  const { scheduleId, streamerName } = useParams<{
    scheduleId?: string
    streamerName?: string
  }>()
  const { account } = useAccount()

  const isImUser = GROUP.USER.includes(account?.viewer?.role!)

  return (
    <tr key={stockCounsel.id}>
      <td>
        <span
          className={
            stockCounsel.stockCounselStatus === "END" ? "ty01" : "ty02"
          }
        >
          {(["WAITING", "RECORDING"] as Status[]).includes(
            stockCounsel.stockCounselStatus,
          ) ? (
            <a
              href="#!"
              onClick={(e: MouseEvent) =>
                recording(e, stockCounsel.stockCounselStatus, stockCounsel.id)
              }
            >
              {getLabelByStatus(stockCounsel.stockCounselStatus)}
            </a>
          ) : isImUser ? (
            <CustomLink
              to={`/${streamerName}/${scheduleId}/stock_counsels/${stockCounsel.id}`}
              target="_blank"
              title="재생"
            />
          ) : (
            "완료"
          )}
        </span>
      </td>
      <td className="center">
        <a href="#!" onClick={(e: MouseEvent) => e.preventDefault()}>
          {stockCounsel.stockName}
        </a>
      </td>
      <td>{stockCounsel.requestCount}</td>
    </tr>
  )
}

const getLabelByStatus = (status: Status) => {
  switch (status) {
    case "RECORDING":
      return "진행"
    case "END":
      return "재생"
    case "WAITING":
    default:
      return "대기"
  }
}

export interface StockCounsel {
  id: number
  vodId: number
  stockName: string
  requestCount: number
  stockCounselStatus: Status
  createdAt: string
  vod?: Vod
}

type Status = "WAITING" | "RECORDING" | "END"
